import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DemoRequestInputSchema,
  DEMO_REQUEST_INTEREST_LABELS,
  type DemoRequestInterest,
} from '@starter/shared';
import { ApiError, submitDemoRequest } from '../../api/client.js';
import { PublicFooter } from '../../components/public/PublicFooter.js';
import { PublicNav } from '../../components/public/PublicNav.js';

const INTEREST_OPTIONS: DemoRequestInterest[] = [
  'REQUEST_DEMO',
  'PRICING',
  'MORE_INFORMATION',
  'OTHER',
];

const DEMO_TOPICS = [
  'Student clinical documentation',
  'Case and specialty tracking',
  'Faculty review and approvals',
  'Graduation requirement progress',
  'Program administration',
];

const DEMO_FORM_FIELD_ORDER = [
  'fullName',
  'institution',
  'workEmail',
  'phone',
  'jobTitle',
  'program',
  'interest',
  'message',
] as const;

function fieldErrorId(name: string): string {
  return `${name}-error`;
}

export function RequestDemoPage() {
  useEffect(() => {
    document.title = 'Request a Demo — ScrubLedger';
  }, []);

  const validationAlertRef = useRef<HTMLDivElement>(null);

  const [fullName, setFullName] = useState('');
  const [institution, setInstitution] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [program, setProgram] = useState('');
  const [interest, setInterest] = useState<DemoRequestInterest>('REQUEST_DEMO');
  const [message, setMessage] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [validationSummary, setValidationSummary] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setValidationSummary(null);

    const parsed = DemoRequestInputSchema.safeParse({
      fullName,
      institution,
      workEmail,
      phone,
      jobTitle,
      program,
      interest,
      message,
      companyWebsite,
    });

    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]?.toString() ?? 'form';
        if (!errs[key]) errs[key] = issue.message;
      }
      setFieldErrors(errs);
      const firstKey = DEMO_FORM_FIELD_ORDER.find((key) => errs[key]);
      const firstMessage =
        (firstKey ? errs[firstKey] : undefined) ?? 'Please correct the errors below.';
      setValidationSummary(firstMessage);
      queueMicrotask(() => {
        validationAlertRef.current?.focus();
        if (firstKey) {
          const el = document.querySelector<HTMLElement>(`[name="${firstKey}"]`);
          el?.focus();
        }
      });
      return;
    }

    setSubmitting(true);
    try {
      await submitDemoRequest(parsed.data);
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'We could not submit your request. Please try again or email support@scrubledger.com.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="public-page">
      <PublicNav />
      <main className="public-simple-page public-simple-page--wide">
        <div className="public-simple-page__card public-demo-card">
          {submitted ? (
            <div className="public-demo-success" role="status">
              <h1>Thanks — we received your request.</h1>
              <p>
                A member of the ScrubLedger team will contact you shortly to schedule a demonstration
                and answer any questions about your program.
              </p>
              <Link to="/" className="btn btn--ghost">Back to home</Link>
            </div>
          ) : (
            <>
              <h1>See ScrubLedger in action</h1>
              <p className="public-demo-intro">
                ScrubLedger helps Surgical Technology programs reduce clinical paperwork, simplify case
                tracking and faculty review, and gain visibility into student clinical progress and
                graduation requirements. Request a personalized demo for your program.
              </p>

              <section className="public-demo-topics" aria-labelledby="demo-topics-heading">
                <h2 id="demo-topics-heading">What we&apos;ll cover during your demo</h2>
                <ul className="public-list public-list--check">
                  {DEMO_TOPICS.map((topic) => (
                    <li key={topic}>{topic}</li>
                  ))}
                </ul>
              </section>

              {error && (
                <div className="banner banner--error" role="alert">
                  {error}
                </div>
              )}

              <form className="public-demo-form" onSubmit={handleSubmit} noValidate>
                {validationSummary && (
                  <div
                    ref={validationAlertRef}
                    className="banner banner--error"
                    role="alert"
                    tabIndex={-1}
                  >
                    {validationSummary}
                  </div>
                )}

                <div className="public-demo-form__honeypot" aria-hidden="true">
                  <label>
                    Company website
                    <input
                      type="text"
                      name="companyWebsite"
                      tabIndex={-1}
                      autoComplete="off"
                      value={companyWebsite}
                      onChange={(e) => setCompanyWebsite(e.target.value)}
                    />
                  </label>
                </div>

                <label className="field">
                  <span className="field__label">Full name</span>
                  <input
                    type="text"
                    name="fullName"
                    autoComplete="name"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.fullName}
                    aria-describedby={fieldErrors.fullName ? fieldErrorId('fullName') : undefined}
                  />
                  {fieldErrors.fullName && (
                    <span className="field__error" id={fieldErrorId('fullName')}>
                      {fieldErrors.fullName}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field__label">School / Institution</span>
                  <input
                    type="text"
                    name="institution"
                    autoComplete="organization"
                    required
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.institution}
                    aria-describedby={
                      fieldErrors.institution ? fieldErrorId('institution') : undefined
                    }
                  />
                  {fieldErrors.institution && (
                    <span className="field__error" id={fieldErrorId('institution')}>
                      {fieldErrors.institution}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field__label">Work email</span>
                  <input
                    type="email"
                    name="workEmail"
                    autoComplete="email"
                    required
                    value={workEmail}
                    onChange={(e) => setWorkEmail(e.target.value)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.workEmail}
                    aria-describedby={fieldErrors.workEmail ? fieldErrorId('workEmail') : undefined}
                  />
                  {fieldErrors.workEmail && (
                    <span className="field__error" id={fieldErrorId('workEmail')}>
                      {fieldErrors.workEmail}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field__label">Phone (optional)</span>
                  <input
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.phone}
                    aria-describedby={fieldErrors.phone ? fieldErrorId('phone') : undefined}
                  />
                  {fieldErrors.phone && (
                    <span className="field__error" id={fieldErrorId('phone')}>
                      {fieldErrors.phone}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field__label">Job title / role (optional)</span>
                  <input
                    type="text"
                    name="jobTitle"
                    autoComplete="organization-title"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.jobTitle}
                    aria-describedby={fieldErrors.jobTitle ? fieldErrorId('jobTitle') : undefined}
                  />
                  {fieldErrors.jobTitle && (
                    <span className="field__error" id={fieldErrorId('jobTitle')}>
                      {fieldErrors.jobTitle}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field__label">Program (optional)</span>
                  <input
                    type="text"
                    name="program"
                    placeholder="Surgical Technology"
                    value={program}
                    onChange={(e) => setProgram(e.target.value)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.program}
                    aria-describedby={fieldErrors.program ? fieldErrorId('program') : undefined}
                  />
                  {fieldErrors.program && (
                    <span className="field__error" id={fieldErrorId('program')}>
                      {fieldErrors.program}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field__label">I&apos;m interested in</span>
                  <select
                    name="interest"
                    required
                    value={interest}
                    onChange={(e) => setInterest(e.target.value as DemoRequestInterest)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.interest}
                    aria-describedby={fieldErrors.interest ? fieldErrorId('interest') : undefined}
                  >
                    {INTEREST_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {DEMO_REQUEST_INTEREST_LABELS[value]}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.interest && (
                    <span className="field__error" id={fieldErrorId('interest')}>
                      {fieldErrors.interest}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field__label">Message (optional)</span>
                  <textarea
                    name="message"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors.message}
                    aria-describedby={fieldErrors.message ? fieldErrorId('message') : undefined}
                  />
                  {fieldErrors.message && (
                    <span className="field__error" id={fieldErrorId('message')}>
                      {fieldErrors.message}
                    </span>
                  )}
                </label>

                <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Request My Demo'}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
